import { useCallback, useState } from "react";
import { SubAssembly, LandingGearType } from "../../../../types/subAssembly";
import { normalizeOffWingTypeForForm } from "../../../../utils/offWingType";
import { getSubAssemblyOffWingType } from "../../../../utils/subAssemblyUtils";

type SubAssemblyForm = Partial<SubAssembly> & { airframeFamily?: string };

export function useSubAssemblyForm() {
  const buildDefaultForm = useCallback(
    (): SubAssemblyForm => ({
      airframeFamily: "",
      landingGearType: "RMLG" as LandingGearType,
      subAssemblyType: "",
      serialNumber: "",
      partNumber: "",
      installDate: "",
      offWingType: "In Shop",
      shopVisitReasonId: "",
      shopVisitReasonLabel: "",
    }),
    []
  );

  const [form, setForm] = useState<SubAssemblyForm>(() => buildDefaultForm());
  const [selected, setSelected] = useState<SubAssembly | null>(null);
  const [isCreate, setIsCreate] = useState(false);
  const [open, setOpen] = useState(false);

  const create = useCallback(() => {
    setSelected(null);
    setForm(buildDefaultForm());
    setIsCreate(true);
    setOpen(true);
  }, [buildDefaultForm]);

  const edit = useCallback((subAssembly: SubAssembly) => {
    setSelected(subAssembly);

    let installDate = subAssembly.installDate || "";
    if (installDate && installDate.includes("T")) {
      installDate = installDate.split("T")[0];
    }

    const resolvedOffWing = normalizeOffWingTypeForForm(
      getSubAssemblyOffWingType(subAssembly) ?? subAssembly.offWingType
    );

    setForm({
      ...subAssembly,
      installDate,
      airframeFamily: subAssembly.airframeFamily ?? "",
      offWingType: resolvedOffWing,
      shopVisitReasonId: subAssembly.shopVisitReasonId ?? "",
      shopVisitReasonLabel: subAssembly.shopVisitReasonLabel ?? "",
    });
    setIsCreate(false);
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const change = useCallback((key: keyof SubAssemblyForm, value: any) => {
    setForm((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "landingGearType") {
        updated.subAssemblyType = "";
      }
      if (key === "airframeFamily") {
        updated.subAssemblyType = "";
      }
      if (key === "offWingType" && value !== "In Shop") {
        updated.shopVisitReasonId = "";
        updated.shopVisitReasonLabel = "";
      }
      return updated;
    });
  }, []);

  return {
    form,
    selected,
    isCreate,
    open,
    create,
    edit,
    close,
    change,
  };
}
